import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {List} from 'immutable';

import {getModelForResourceType} from 'resources/modelRegistry';

import {Badge, LoadingSpinner, NoDataMsg, Pagination, Table, WithTooltip} from '@albert-io/atomic';

import {sortOptions} from '@albert-io/atomic/organisms/Table/Header/Header.react';

import awaitMandarkQueries from 'lib/hocs/awaitMandarkQueries';

import {getSchoolAccountsQuery} from '../shared';

import SchoolAccountsActionsDropdown from '../SchoolAccountsActionsDropdown/SchoolAccountsActionsDropdown.react';

import '../school-accounts.scss';

const pageSize = 25;

const getters = {
  email: 'getEmail',
  first_name: 'getFirstName',
  last_name: 'getLastName'
};

function getSearchFilters(query, term) {
  const terms = term.split(' ').filter((str) => str); // Filter out any spaces
  const queryWithFilters = terms.reduce((prev, curr) => {
    const encodedTerm = encodeURIComponent(curr);
    return prev.filter({
      included: {
        'classrooms_v1.students_v2': {
          all_of: [
            {
              any_of: [
                {
                  first_name: {
                    case_insensitive_substring: encodedTerm
                  }
                },
                {
                  last_name: {
                    case_insensitive_substring: encodedTerm
                  }
                },
                {
                  email: {
                    case_insensitive_substring: encodedTerm
                  }
                }
              ]
            }
          ]
        },
        'managed_users_v1.student_v2': {
          all_of: [
            {
              any_of: [
                {
                  first_name: {
                    case_insensitive_substring: encodedTerm
                  }
                },
                {
                  last_name: {
                    case_insensitive_substring: encodedTerm
                  }
                },
                {
                  email: {
                    case_insensitive_substring: encodedTerm
                  }
                }
              ]
            }
          ]
        }
      }
    });
  }, query);

  return queryWithFilters;
}

const SchoolAccountsTable = ({school}) => {
  const [page, setPage] = useState(1);
  const [sortByParam, setSortByParam] = useState('last_name');
  const [sortDirection, setSortDirection] = useState(sortOptions.ASCENDING);

  const enrolledUsers = school.getClassrooms().reduce((prev, curr) => {
    return curr.getStudents().isEmpty() ? prev : prev.concat(curr.getStudents());
  }, List());

  const unenrolledUsers = school.getManagedUsers().reduce((prev, curr) => {
    return curr.getStudent().isEmpty() ? prev : prev.push(curr.getStudent());
  }, List());

  const getter = getters[sortByParam];

  const users = enrolledUsers
    .concat(unenrolledUsers)
    .groupBy((item) => item.getId())
    .valueSeq()
    .map((val) => val.first())
    .sortBy((item) => item[getter]().toLowerCase());

  let usersToShow = users.sortBy((item) => item[getter]().toLowerCase());

  usersToShow = sortDirection === sortOptions.ASCENDING ? usersToShow : usersToShow.reverse();

  const start = page * pageSize - pageSize;
  const end = start + pageSize;

  const totalPages = Math.ceil(usersToShow.size / pageSize);

  const paginatedUsersToShow = usersToShow.slice(start, end);

  return users.isEmpty() ? (
    <NoDataMsg.Container centered className='u-mar-lr_auto' paddingLevel={6}>
      <NoDataMsg.Body>No matches found</NoDataMsg.Body>
    </NoDataMsg.Container>
  ) : (
    <>
      <Table
        className='school-accounts__table'
        // eslint-disable-next-line no-shadow
        customSortFunc={({sortByParam, sortDirection}) => {
          if (sortByParam) {
            setSortByParam(sortByParam);
          }
          if (sortDirection) {
            setSortDirection(sortDirection);
          }
        }}
        data={paginatedUsersToShow}
        defaultSortDirection={sortDirection}
        key={`${page}${sortByParam}${sortDirection}`}
        theme='list'
      >
        <Table.Column
          content={(student) => student.getLastName()}
          defaultSortParam={sortByParam === 'last_name'}
          heading='Last Name'
          sortable
          sortBy='last_name'
        />
        <Table.Column
          content={(student) => student.getFirstName()}
          defaultSortParam={sortByParam === 'first_name'}
          heading='First Name'
          sortable
          sortBy='first_name'
        />
        <Table.Column
          className='u-word-break_break-all'
          content={(student) => student.getEmail()}
          defaultSortParam={sortByParam === 'email'}
          heading='Email'
          sortable
          sortBy='email'
        />
        <Table.Column
          align='center'
          className='school-accounts__badge-wrapper'
          content={() => (
            <WithTooltip content={`Managed by ${school.getName()}`}>
              <Badge className='u-mar-r_2' iconAlign='center' icon={['far', 'school']} />
            </WithTooltip>
          )}
          isActionMenu
        />
        <Table.Column
          align='center'
          content={(student) => <SchoolAccountsActionsDropdown student={student} />}
          isActionMenu
        />
      </Table>
      <div className='u-pad_2'>
        <Pagination
          align='right'
          defaultPage={page}
          key={page}
          totalPages={totalPages}
          position='top'
          onChange={(val) => setPage(val)}
        />
      </div>
    </>
  );
};

SchoolAccountsTable.propTypes = {
  school: PropTypes.instanceOf(getModelForResourceType('school_v5'))
};

export default awaitMandarkQueries((props) => {
  const {search = ''} = props.location.query;
  const query = getSchoolAccountsQuery(props.params.schoolId);
  return {
    loadingElement: <LoadingSpinner className='u-mar-tb_4' size={2} />,
    queries: {
      school: getSearchFilters(query, search)
    }
  };
}, SchoolAccountsTable);
